import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Bell, Home, BookOpen, FileText, User, LogOut, PlusCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import TranslatorOverview from './translator/TranslatorOverview';
import TranslatorBookRequests from './translator/TranslatorBookRequests';
import TranslatorCurrentWork from './translator/TranslatorCurrentWork';
import NotificationPanel from '../components/NotificationPanel';
import UserProfile from './shared/UserProfile';

const TranslatorDashboard = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [stats, setStats] = useState({
    pending_requests: 0,
    assigned_books: 0,
    completed_books: 0,
    rejection_count: 0
  });
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/translator/overview`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setUnreadCount(response.data.filter(n => !n.read).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchUnreadCount();
    const interval = setInterval(() => {
      fetchStats();
      fetchUnreadCount();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const navItems = [
    { path: '/translator', icon: Home, label: 'Oorsig', badge: null },
    { path: '/translator/book-requests', icon: PlusCircle, label: 'Boek Aanvrae', badge: stats.pending_requests },
    { path: '/translator/current-work', icon: FileText, label: 'Huidige Werk', badge: null },
    { path: '/translator/profiel', icon: User, label: 'Profiel', badge: null },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="translator-dashboard">
      {/* Sidebar */}
      <aside className="w-64 bg-white shadow-lg fixed h-full">
        <div className="p-6 border-b">
          <h1 className="text-xl font-bold text-green-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Die Biblioteek
          </h1>
          <p className="text-sm text-gray-500">Vertaler Portaal</p>
        </div>
        
        <nav className="p-4 space-y-2">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                location.pathname === item.path
                  ? 'bg-green-50 text-green-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <item.icon className="h-5 w-5" />
              <span>{item.label}</span>
              {item.badge > 0 && (
                <Badge variant="secondary" className="ml-auto bg-green-100 text-green-600">
                  {item.badge}
                </Badge>
              )}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 w-full p-4 border-t">
          <Button
            variant="ghost"
            className="w-full justify-start text-red-600 hover:bg-red-50"
            onClick={logout}
            data-testid="logout-btn"
          >
            <LogOut className="h-5 w-5 mr-3" />
            Teken Uit
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-8">
        {/* Header */}
        <header className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Welkom, {user?.voornaam}!
            </h2>
            <p className="text-gray-500">
              {stats.rejection_count > 0 && (
                <span className="text-amber-600">
                  Verwerpings: {stats.rejection_count}/3 
                  {stats.rejection_count >= 3 && ' - Waarskuwing!'}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative"
              data-testid="notifications-btn"
            >
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </Button>
            {showNotifications && (
              <NotificationPanel onClose={() => setShowNotifications(false)} />
            )}
          </div>
        </header>

        {/* Routes */}
        <Routes>
          <Route index element={<TranslatorOverview stats={stats} onUpdate={fetchStats} />} />
          <Route path="book-requests" element={<TranslatorBookRequests onUpdate={fetchStats} />} />
          <Route path="current-work" element={<TranslatorCurrentWork />} />
          <Route path="profiel" element={<UserProfile />} />
        </Routes>
      </main>
    </div>
  );
};

export default TranslatorDashboard;
